/**
 * Profitability tab for the Admin Dashboard.
 * Extracted from AdminDashboard.jsx (was ~250 inline lines).
 *
 * Loads /api/admin/profitability?days=N, lets admin tune per-item costs
 * (ingredients/labor/packaging/other) + target margin %, computes live
 * margin in JS, and persists changes via PATCH /api/admin/menu/{id}/costs.
 */
import React, { useEffect, useState } from "react";
import { TrendingUp } from "lucide-react";
import api from "../../api";
import { toast } from "sonner";

const CostInput = ({ value, onChange, testId, suffix }) => (
    <div className="relative">
        <input
            data-testid={testId}
            type="number"
            step="0.01"
            min="0"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-20 bg-earth-50 border border-earth-200 rounded-lg px-2 py-1.5 text-center text-sm focus:border-terracotta-500 outline-none"
        />
        {suffix && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-earth-500 pointer-events-none">
                {suffix}
            </span>
        )}
    </div>
);

const KPI = ({ label, value, testId, accent }) => {
    const colors = { leaf: "text-leaf-600", sand: "text-sand-700", red: "text-red-600" };
    return (
        <div data-testid={testId} className="bg-white border border-earth-200 rounded-2xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-earth-500 font-bold">{label}</div>
            <div className={`font-serif font-black text-xl mt-1 ${accent ? colors[accent] : "text-earth-900"}`}>
                {value}
            </div>
        </div>
    );
};

const ProfitabilityPanel = () => {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);
    const [drafts, setDrafts] = useState({});
    const [savingId, setSavingId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const r = await api.get(`/admin/profitability?days=${days}`);
                if (cancelled) return;
                setData(r.data);
                setDrafts({});
            } catch (err) {
                if (!cancelled) toast.error(err?.response?.data?.detail || "No se pudo cargar");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [days]);

    const setDraft = (id, field, value) =>
        setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [field]: value } }));

    const liveBreakdown = (row) => {
        const draft = drafts[row.id] || {};
        const ci = parseFloat(draft.cost_ingredients ?? row.cost_ingredients) || 0;
        const cl = parseFloat(draft.cost_labor ?? row.cost_labor) || 0;
        const cp = parseFloat(draft.cost_packaging ?? row.cost_packaging) || 0;
        const co = parseFloat(draft.cost_other ?? row.cost_other) || 0;
        const price = parseFloat(draft.price ?? row.price) || 0;
        const target = parseFloat(draft.target_margin_pct ?? row.target_margin_pct) || 60;
        const total_cost = +(ci + cl + cp + co).toFixed(2);
        const margin = +(price - total_cost).toFixed(2);
        const margin_pct = price > 0 ? +((margin / price) * 100).toFixed(1) : 0;
        const suggested = target > 0 && target < 100 ? +(total_cost / (1 - target / 100)).toFixed(2) : total_cost;
        return { total_cost, price, margin, margin_pct, target, suggested };
    };

    const reload = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/profitability?days=${days}`);
            setData(r.data);
            setDrafts({});
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo cargar");
        } finally {
            setLoading(false);
        }
    };

    const saveRow = async (row) => {
        const draft = drafts[row.id];
        if (!draft) return;
        setSavingId(row.id);
        try {
            await api.patch(`/admin/menu/${row.id}/costs`, draft);
            toast.success(`Guardado: ${row.name_es}`);
            await reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al guardar");
        } finally {
            setSavingId(null);
        }
    };

    const applySuggestedPrice = (row) => {
        const bd = liveBreakdown(row);
        setDraft(row.id, "price", bd.suggested.toString());
    };

    if (loading && !data) {
        return (
            <div data-testid="profitability-loading" className="text-earth-600">
                Cargando análisis de rentabilidad…
            </div>
        );
    }
    if (!data) return null;
    const agg = data.aggregate;

    return (
        <section data-testid="profitability-panel" className="space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h2 className="font-serif text-2xl font-bold text-earth-900 inline-flex items-center gap-2">
                        <TrendingUp className="w-6 h-6 text-leaf-500" />
                        Análisis de Rentabilidad
                    </h2>
                    <p className="text-sm text-earth-600 mt-1">
                        Edita los costos para ver márgenes en tiempo real. Costo de delivery por orden:{" "}
                        <b>${data.delivery_cost_per_order.toFixed(2)}</b>
                    </p>
                </div>
                <div className="inline-flex items-center gap-1 bg-white border border-earth-200 rounded-full p-1">
                    {[7, 30, 90, 365].map((d) => (
                        <button
                            key={d}
                            data-testid={`profit-window-${d}-btn`}
                            onClick={() => setDays(d)}
                            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                                days === d ? "bg-leaf-500 text-white" : "text-earth-600 hover:bg-earth-100"
                            }`}
                        >
                            {d === 365 ? "1A" : `${d}D`}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <KPI label={`Ingresos · ${days}d`} value={`$${agg.revenue.toFixed(2)}`} testId="kpi-revenue" />
                <KPI label="COGS (insumos)" value={`$${agg.cogs.toFixed(2)}`} testId="kpi-cogs" />
                <KPI label="Costo delivery" value={`$${agg.delivery_cost.toFixed(2)}`} testId="kpi-delivery-cost" />
                <KPI label="Ganancia bruta" value={`$${agg.gross_profit.toFixed(2)}`} testId="kpi-profit" accent={agg.gross_profit >= 0 ? "leaf" : "red"} />
                <KPI label="Margen promedio" value={`${agg.avg_margin_pct.toFixed(1)}%`} testId="kpi-margin" accent={agg.avg_margin_pct >= 50 ? "leaf" : agg.avg_margin_pct >= 30 ? "sand" : "red"} />
            </div>

            <div className="bg-white border border-earth-200 rounded-3xl overflow-hidden">
                <div className="px-6 py-4 border-b border-earth-100">
                    <h3 className="font-serif font-bold text-earth-900">Costos por plato</h3>
                    <p className="text-xs text-earth-500 mt-0.5">
                        Cambia cualquier celda y pulsa <b>Guardar</b>. Precio sugerido se calcula con
                        <code className="px-1 mx-1 bg-earth-100 rounded">precio = costo / (1 − margen%/100)</code>.
                    </p>
                </div>
                <div className="overflow-x-auto">
                    <table data-testid="profit-table" className="w-full text-sm border-collapse">
                        <thead className="bg-earth-50 text-[10px] uppercase tracking-wider font-bold text-earth-600">
                            <tr>
                                <th className="text-left p-3 sticky left-0 bg-earth-50">Plato</th>
                                <th className="p-2">Insumos $</th>
                                <th className="p-2">Mano de obra $</th>
                                <th className="p-2">Empaque $</th>
                                <th className="p-2">Otros $</th>
                                <th className="p-2 bg-earth-100">Costo total</th>
                                <th className="p-2">Precio $</th>
                                <th className="p-2 bg-earth-100">Margen $</th>
                                <th className="p-2 bg-earth-100">Margen %</th>
                                <th className="p-2">Obj. %</th>
                                <th className="p-2">Sugerido</th>
                                <th className="p-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {data.per_item.map((row) => {
                                const bd = liveBreakdown(row);
                                const dirty = !!drafts[row.id];
                                const marginClr =
                                    bd.margin_pct >= 50 ? "text-leaf-600" : bd.margin_pct >= 30 ? "text-sand-600" : "text-red-600";
                                return (
                                    <tr key={row.id} data-testid={`profit-row-${row.slug}`}
                                        className={`border-t border-earth-100 transition-colors ${dirty ? "bg-yellow-50/50" : "hover:bg-earth-50/50"}`}>
                                        <td className="p-3 min-w-[180px] sticky left-0 bg-inherit">
                                            <div className="flex items-center gap-3">
                                                <img src={row.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
                                                <div className="leading-tight">
                                                    <div className="font-bold text-earth-900 text-[13px]">{row.name_es}</div>
                                                    <div className="text-[10px] text-earth-500">
                                                        {row.available ? "Disponible" : "Pre-orden"}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-1.5"><CostInput value={drafts[row.id]?.cost_ingredients ?? row.cost_ingredients} onChange={(v) => setDraft(row.id, "cost_ingredients", v)} testId={`cost-ingredients-${row.slug}`} /></td>
                                        <td className="p-1.5"><CostInput value={drafts[row.id]?.cost_labor ?? row.cost_labor} onChange={(v) => setDraft(row.id, "cost_labor", v)} testId={`cost-labor-${row.slug}`} /></td>
                                        <td className="p-1.5"><CostInput value={drafts[row.id]?.cost_packaging ?? row.cost_packaging} onChange={(v) => setDraft(row.id, "cost_packaging", v)} testId={`cost-packaging-${row.slug}`} /></td>
                                        <td className="p-1.5"><CostInput value={drafts[row.id]?.cost_other ?? row.cost_other} onChange={(v) => setDraft(row.id, "cost_other", v)} testId={`cost-other-${row.slug}`} /></td>
                                        <td className="p-2 text-center bg-earth-50 font-bold text-earth-900" data-testid={`total-cost-${row.slug}`}>
                                            ${bd.total_cost.toFixed(2)}
                                        </td>
                                        <td className="p-1.5"><CostInput value={drafts[row.id]?.price ?? row.price} onChange={(v) => setDraft(row.id, "price", v)} testId={`price-${row.slug}`} /></td>
                                        <td className={`p-2 text-center bg-earth-50 font-bold ${marginClr}`} data-testid={`margin-abs-${row.slug}`}>${bd.margin.toFixed(2)}</td>
                                        <td className={`p-2 text-center bg-earth-50 font-bold ${marginClr}`} data-testid={`margin-pct-${row.slug}`}>{bd.margin_pct.toFixed(1)}%</td>
                                        <td className="p-1.5"><CostInput value={drafts[row.id]?.target_margin_pct ?? row.target_margin_pct} onChange={(v) => setDraft(row.id, "target_margin_pct", v)} testId={`target-${row.slug}`} suffix="%" /></td>
                                        <td className="p-2 text-center text-leaf-700 font-bold" data-testid={`suggested-${row.slug}`}>
                                            <button type="button" onClick={() => applySuggestedPrice(row)} className="hover:underline" title="Usar precio sugerido">
                                                ${bd.suggested.toFixed(2)}
                                            </button>
                                        </td>
                                        <td className="p-2">
                                            <button data-testid={`save-row-${row.slug}-btn`} onClick={() => saveRow(row)} disabled={!dirty || savingId === row.id}
                                                className={`px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all ${
                                                    dirty ? "bg-terracotta-500 hover:bg-terracotta-600 text-white" : "bg-earth-100 text-earth-400 cursor-not-allowed"
                                                }`}>
                                                {savingId === row.id ? "..." : "Guardar"}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {data.top_sellers.length > 0 && (
                <div className="bg-white border border-earth-200 rounded-3xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-earth-100">
                        <h3 className="font-serif font-bold text-earth-900">
                            Top platos por ganancia · últimos {days} días
                        </h3>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="bg-earth-50 text-[10px] uppercase tracking-wider font-bold text-earth-600">
                            <tr>
                                <th className="text-left p-3">Plato</th>
                                <th className="p-2 text-right">Unidades</th>
                                <th className="p-2 text-right">Ingresos</th>
                                <th className="p-2 text-right">COGS</th>
                                <th className="p-2 text-right">Ganancia</th>
                                <th className="p-2 text-right">Margen %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.top_sellers.map((t, i) => (
                                <tr key={t.item_id || `top-${i}`} data-testid={`top-seller-${i}`}
                                    className="border-t border-earth-100 hover:bg-earth-50/40">
                                    <td className="p-3 font-semibold text-earth-900">{t.name}</td>
                                    <td className="p-2 text-right">{t.units}</td>
                                    <td className="p-2 text-right">${t.revenue.toFixed(2)}</td>
                                    <td className="p-2 text-right">${t.cogs.toFixed(2)}</td>
                                    <td className={`p-2 text-right font-bold ${t.gross_profit >= 0 ? "text-leaf-600" : "text-red-600"}`}>
                                        ${t.gross_profit.toFixed(2)}
                                    </td>
                                    <td className="p-2 text-right font-bold text-earth-700">{t.margin_pct.toFixed(1)}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export default ProfitabilityPanel;
